import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react';
import { errorMessage } from '../../shared/api/client';
import { Alert, Button } from '../../shared/ui/components';

export type MenuAction = { label: string; onSelect: () => void; danger?: boolean };

/**
 * Menú del clic derecho de la consola (cantos, videos, imágenes y lo de Programa). Se cierra con
 * Esc, con un clic fuera o al elegir algo.
 */
export function useContextMenu() {
  const [menu, setMenu] = useState<{ x: number; y: number; title: string; actions: MenuAction[] } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Que no se salga de la ventana: se mide ya pintado y se sube o se recorre si hace falta.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!menu || !el) return;
    const r = el.getBoundingClientRect();
    el.style.left = `${Math.max(4, Math.min(menu.x, window.innerWidth - r.width - 4))}px`;
    el.style.top = `${Math.max(4, Math.min(menu.y, window.innerHeight - r.height - 4))}px`;
  }, [menu]);

  useEffect(() => {
    if (!menu) return;
    ref.current?.querySelector('button')?.focus();
    const close = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const onDown = (e: Event) => !ref.current?.contains(e.target as Node) && close();
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('resize', close);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('blur', close);
    };
  }, [menu]);

  const open = (e: MouseEvent, title: string, actions: MenuAction[]) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, title, actions });
  };

  const element = menu ? (
    <div ref={ref} className="ctxmenu" role="menu" aria-label={menu.title} style={{ left: menu.x, top: menu.y }}>
      <p className="ctxmenu__title">{menu.title}</p>
      {menu.actions.map((a) => (
        <button
          key={a.label}
          type="button"
          role="menuitem"
          className={`ctxmenu__item${a.danger ? ' ctxmenu__item--danger' : ''}`}
          onClick={() => {
            setMenu(null);
            a.onSelect();
          }}
        >
          {a.label}
        </button>
      ))}
    </div>
  ) : null;

  return { open, element };
}

export type DialogField = { name: string; label: string; value: string; multiline?: boolean; required?: boolean };

/** Ventanita para editar (letra de un canto, nombre de un archivo): sin window.prompt, que la app no tiene. */
export function EditDialog({ title, fields, onSave, onClose, children }: { title: string; fields: DialogField[]; onSave: (values: Record<string, string>) => Promise<void>; onClose: () => void; children?: ReactNode }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const values = Object.fromEntries(fields.map((f) => [f.name, String(data.get(f.name) ?? '')]));
    setBusy(true);
    setError(null);
    try {
      await onSave(values);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="ctxdialog" role="presentation" onPointerDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <form className="ctxdialog__box" role="dialog" aria-modal="true" aria-label={title} onSubmit={submit}>
        <h2 className="ctxdialog__title">{title}</h2>
        {error ? <Alert>{error}</Alert> : null}
        {children}
        {fields.map((f, i) => (
          <label key={f.name} className="ctxdialog__field">
            {f.label}
            {f.multiline ? <textarea name={f.name} defaultValue={f.value} rows={14} autoFocus={i === 0} required={f.required} /> : <input name={f.name} defaultValue={f.value} autoFocus={i === 0} required={f.required} />}
          </label>
        ))}
        <div className="ctxdialog__actions">
          <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" busy={busy}>
            Guardar
          </Button>
        </div>
      </form>
    </div>
  );
}
